/**
 * Sidebar display mode — the sidebar's answer to the Theme control's
 * System / Light / Dark.
 *
 * `auto` is the same *shape* as "System": a mode that defers to context (here,
 * the pointer) instead of pinning a value. That parity is deliberate — the two
 * controls sit in the same menu and should read as one idea, not two.
 *
 * ## Why localStorage and not the profile row
 *
 * This is a per-DEVICE preference, not a per-user one. The right sidebar mode on
 * a 13" laptop is not the right one on a 27" monitor, so syncing it across a
 * user's devices would actively fight them. Contrast the theme preference and
 * tour progress, both of which ARE server-backed because they should follow the
 * person.
 */
export type SidebarDisplayMode = 'auto' | 'expanded' | 'collapsed'

export const SIDEBAR_DISPLAY_MODE_STORAGE_KEY = 'vera:sidebar:mode'

/** Transient hover state in `auto`. Pre-dates this feature; still read/written. */
export const SIDEBAR_OPEN_STORAGE_KEY = 'vera:sidebar:open'

/**
 * Retired by VRG-248. The padlock's boolean could only express two of the three
 * modes, so it is read ONCE, mapped onto a mode, and deleted — a user who was
 * pinned open lands on `expanded` rather than being silently dropped back to
 * hover-reactive.
 */
const LEGACY_LOCK_STORAGE_KEY = 'vera:sidebar:locked'

export const SIDEBAR_DISPLAY_MODES: readonly SidebarDisplayMode[] = ['auto', 'expanded', 'collapsed']

const isDisplayMode = (value: unknown): value is SidebarDisplayMode =>
  typeof value === 'string' && (SIDEBAR_DISPLAY_MODES as readonly string[]).includes(value)

/**
 * Resolve the stored mode, migrating the retired padlock key on first read.
 *
 * Order matters: an already-written mode always wins, so the migration cannot
 * re-run and clobber a later choice even if the legacy key somehow reappears.
 * Every access is wrapped because localStorage throws outright in Safari private
 * browsing and when site data is blocked — an unusable sidebar is a far worse
 * failure than losing the preference.
 */
export const readSidebarDisplayMode = (): SidebarDisplayMode => {
  if (typeof window === 'undefined') return 'auto'

  try {
    const stored = window.localStorage.getItem(SIDEBAR_DISPLAY_MODE_STORAGE_KEY)
    if (isDisplayMode(stored)) return stored

    const legacyLocked = window.localStorage.getItem(LEGACY_LOCK_STORAGE_KEY)
    if (legacyLocked !== null) {
      const migrated: SidebarDisplayMode = legacyLocked === 'true' ? 'expanded' : 'auto'
      window.localStorage.setItem(SIDEBAR_DISPLAY_MODE_STORAGE_KEY, migrated)
      window.localStorage.removeItem(LEGACY_LOCK_STORAGE_KEY)
      return migrated
    }
  } catch {
    // Storage unavailable — fall through to the default rather than failing to render.
  }

  return 'auto'
}

export const writeSidebarDisplayMode = (mode: SidebarDisplayMode) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SIDEBAR_DISPLAY_MODE_STORAGE_KEY, mode)
  } catch {
    // See above.
  }
}

export const readSidebarHoverOpen = (fallback: boolean) => {
  if (typeof window === 'undefined') return fallback
  try {
    const stored = window.localStorage.getItem(SIDEBAR_OPEN_STORAGE_KEY)
    return stored === null ? fallback : stored === 'true'
  } catch {
    return fallback
  }
}

export const writeSidebarHoverOpen = (open: boolean) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SIDEBAR_OPEN_STORAGE_KEY, `${open}`)
  } catch {
    // See above.
  }
}

/*
 * The toggle's BINDING used to live here as `SIDEBAR_TOGGLE_KEY = 'B'`. It moved to
 * the shortcut catalog (`utils/shortcutCatalog.ts`) when the binding became
 * user-rebindable: a constant here could only ever describe the default, and every
 * surface reading it would have kept advertising ⌘B after the user changed it.
 * Mode storage stays here; binding storage lives with the other bindings.
 */
